<template>
  <div
    class="employee-column border-right relative"
    style="min-width: 300px; width: 300px"
    @dragover.prevent="onDragOver"
    @drop="onDrop"
    @dragleave="$emit('drag-leave')">
    <div class="column-header glass-header pa-3 d-flex align-center sticky-top z-index-10">
      <v-tooltip bottom color="black">
        <template v-slot:activator="{ on, attrs }">
          <v-badge bottom overlap :color="col.liveStatus.color" dot offset-x="10" offset-y="10" class="mr-3">
            <v-avatar size="48" class="elevation-4 border-light cursor-pointer" v-bind="attrs" v-on="on">
              <img :src="col.picture ? '/img/profilbilder/' + col.picture : '/img/default-avatar.png'" style="object-fit: cover" />
            </v-avatar>
          </v-badge>
        </template>
        <div class="text-center pa-2">
          <div class="font-weight-bold mb-1">{{ col.firstName }}</div>
          <div v-if="col.liveStatus.active" class="green--text"><v-icon x-small color="green">mdi-login</v-icon> Seit {{ col.liveStatus.since }}</div>
          <div v-else class="grey--text text--lighten-1">Nicht anwesend</div>
        </div>
      </v-tooltip>

      <div class="overflow-hidden flex-grow-1">
        <div class="font-weight-bold white--text text-truncate text-h6">{{ col.firstName }}</div>
        <div class="text-caption grey--text d-flex align-center">
          <v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon> {{ col.shiftRange }}
        </div>
      </div>

      <v-menu offset-y left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon small color="grey lighten-1" v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
        </template>
        <v-list dense dark class="grey darken-3">
          <v-list-item @click="$emit('open-note', col)">
            <v-icon small left>mdi-message-text</v-icon> <v-list-item-title>Notiz senden</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div class="lane-body relative" :style="{ height: hoursCount * hourHeight + 80 + 'px' }" ref="laneBody">
      <div v-for="h in hoursCount" :key="'g' + h" class="grid-line" :style="{ top: (h - 1) * hourHeight + headerOffset + 'px' }"></div>

      <div
        v-for="(bg, idx) in col.backgrounds"
        :key="'bg' + idx"
        class="shift-background"
        :style="{ top: bg.top + 'px', height: bg.height + 'px', backgroundColor: bg.bgColor, borderLeft: `3px solid ${bg.color}` }"></div>

      <div
        v-for="(block, bIdx) in col.items"
        :key="'blk' + bIdx"
        class="cockpit-block rounded-lg elevation-4 d-flex flex-column overflow-hidden"
        :class="[block.class, { 'small-block': block.height < 60 }]"
        :style="{ top: block.top + 'px', height: block.height + 'px', borderLeft: `4px solid ${block.borderColor}` }"
        v-ripple
        @click="$emit('click-item', block, col)">
        <div class="block-content pa-2 fill-height d-flex" :class="block.height < 60 ? 'align-center flex-row' : 'flex-column'">
          <div class="d-flex align-center" :class="block.height < 60 ? 'mr-3' : 'justify-space-between w-100 mb-1'">
            <v-icon x-small :color="block.iconColor" class="mr-2">{{ block.icon }}</v-icon>
            <span class="text-caption font-weight-bold opacity-70" style="white-space: nowrap">
              {{ block.start }} <span v-if="block.height >= 60">- {{ block.end }}</span>
            </span>
          </div>
          <div class="overflow-hidden w-100">
            <div class="font-weight-bold text-body-2 text-truncate leading-tight white--text">{{ block.title }}</div>
            <div v-if="block.subtitle && block.height >= 80" class="text-caption grey--text text--lighten-2 text-truncate mt-auto">
              {{ block.subtitle }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isHovered"
        class="ghost-block rounded-lg d-flex align-center justify-center flex-column"
        :class="{ 'error-ghost': hoverState.error }"
        :style="{ top: hoverState.top + 'px', height: hoverState.height + 'px' }">
        <span class="white--text font-weight-bold text-shadow text-h6">{{ hoverState.time }}</span>
        <span v-if="hoverState.error" class="red--text font-weight-bold text-caption bg-black px-2 rounded mt-1">{{ hoverState.error }}</span>
      </div>

      <div class="click-grid-layer" @click.self="$emit('click-empty', $event, col)"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WorkCalendarColumn",
  props: {
    col: Object,
    hoursCount: Number,
    hourHeight: Number,
    headerOffset: Number,
    hoverState: Object, // { colId, time, top, height, error }
  },
  computed: {
    isHovered() {
      return this.hoverState && this.hoverState.colId === this.col.id
    },
  },
  methods: {
    onDragOver(e) {
      // Event an Parent weiterleiten zur Berechnung der Ghost-Position
      // Wir senden clientY relativ zum Lane-Body Start
      const rect = this.$refs.laneBody.getBoundingClientRect()
      const relativeY = e.clientY - rect.top
      this.$emit("drag-over", { event: e, colId: this.col.id, relativeY })
    },
    onDrop(e) {
      this.$emit("drop", { event: e, col: this.col })
    },
  },
}
</script>

<style scoped>
/* Copy styles from previous WorkCalendar.vue */
.employee-column {
  border-right: 1px solid rgba(255, 255, 255, 0.05);
}
.glass-header {
  background: rgba(30, 30, 30, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.border-light {
  border: 2px solid rgba(255, 255, 255, 0.2) !important;
}
.sticky-top {
  position: sticky;
  top: 0;
}
.z-index-10 {
  z-index: 10;
}
.relative {
  position: relative;
}
.grid-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.04);
  pointer-events: none;
}
.shift-background {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 1;
  pointer-events: none;
}
.cockpit-block {
  position: absolute;
  left: 6px;
  right: 6px;
  z-index: 5;
  cursor: pointer;
  transition: transform 0.2s, background 0.2s;
  background: #2a2a2a;
}
.cockpit-block:hover {
  transform: scale(1.02);
  z-index: 6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}
.small-block .block-content {
  padding: 4px 8px !important;
}
.small-block .v-icon {
  font-size: 14px !important;
  margin-right: 4px !important;
}
.small-block .text-caption {
  font-size: 10px !important;
}
.small-block .text-body-2 {
  font-size: 11px !important;
}
.item-pause {
  background: repeating-linear-gradient(45deg, rgba(40, 40, 40, 1), rgba(40, 40, 40, 1) 10px, rgba(50, 50, 50, 1) 10px, rgba(50, 50, 50, 1) 20px);
  border-left-color: #ff9800 !important;
}
.item-system {
  pointer-events: none;
  border-left: 4px solid #d32f2f !important;
  background: repeating-linear-gradient(
    45deg,
    rgba(211, 47, 47, 0.1),
    rgba(211, 47, 47, 0.1) 10px,
    rgba(211, 47, 47, 0.05) 10px,
    rgba(211, 47, 47, 0.05) 20px
  );
}
.glass-purple {
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.15), rgba(156, 39, 176, 0.05));
  border: 1px solid rgba(156, 39, 176, 0.3);
}
.glass-blue {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(33, 150, 243, 0.05));
  border: 1px solid rgba(33, 150, 243, 0.3);
}
.glass-red {
  border: 1px solid rgba(244, 67, 54, 0.3);
}
.ghost-block {
  position: absolute;
  left: 6px;
  right: 6px;
  z-index: 15;
  background: rgba(33, 150, 243, 0.2);
  border: 2px dashed #2196f3;
  pointer-events: none;
  backdrop-filter: blur(2px);
}
.error-ghost {
  background: rgba(244, 67, 54, 0.2);
  border-color: #f44336;
}
.click-grid-layer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  cursor: crosshair;
}
</style>
